import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, publicQuoteUrl, fileUrl } from "@/lib/api";
import { FileText, Send, Eye, CheckCircle2, Clock, XCircle, Plus, Users, Pencil, ExternalLink } from "lucide-react";

const Metric = ({ icon: Icon, label, value, tone = "default", testid }) => {
  const tones = {
    default: "text-zinc-900",
    accent: "text-[#14243D]",
    success: "text-emerald-600",
    warn: "text-amber-600",
    danger: "text-rose-600",
  };
  return (
    <div className="bbh-card bbh-metric p-5 h-full cursor-pointer hover:border-[#14243D] active:scale-[0.98] transition-transform" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          {label}
        </span>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`font-heading text-3xl font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [comprovantes, setComprovantes] = useState([]);
  const [compOpen, setCompOpen] = useState(null); // comprovante aberto p/ visualizar

  const loadComprovantes = async () => {
    try {
      const { data } = await api.get("/comprovantes-pix", { params: { status: "pendente" } });
      setComprovantes(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: r }] = await Promise.all([
          api.get("/orcamentos/stats"),
          api.get("/orcamentos"),
        ]);
        setStats(s);
        setRecent(r.slice(0, 5));
      } catch (e) {
        console.error(e);
      }
    })();
    loadComprovantes();
  }, []);

  const confirmarComp = async (c) => {
    if (!window.confirm(`Confirmar recebimento de R$ ${c.valor.toFixed(2).replace(".", ",")} do orçamento ${c.numero_orcamento}?`)) return;
    try {
      await api.post(`/comprovantes-pix/${c.id}/confirmar`);
      await loadComprovantes();
      setCompOpen(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao confirmar");
    }
  };
  const rejeitarComp = async (c) => {
    const motivo = window.prompt("Motivo da rejeição (opcional):") || "";
    try {
      await api.post(`/comprovantes-pix/${c.id}/rejeitar`, { motivo });
      await loadComprovantes();
      setCompOpen(null);
    } catch { alert("Erro ao rejeitar"); }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
            Painel
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Bem-vindo à BBH
          </h1>
          <p className="text-zinc-500 mt-2">Gestão simples e profissional de orçamentos.</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/clientes"
            data-testid="dashboard-clientes-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
          >
            <Users className="w-4 h-4" /> Clientes
          </Link>
          <Link
            to="/admin/orcamentos?novo=1"
            data-testid="dashboard-novo-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Novo Orçamento
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link to="/admin/orcamentos"><Metric icon={FileText} label="Total" value={stats?.total ?? "-"} testid="metric-total" /></Link>
        <Link to="/admin/orcamentos?status=enviado"><Metric icon={Send} label="Enviados" value={stats?.enviados ?? "-"} testid="metric-enviados" /></Link>
        <Link to="/admin/orcamentos?status=visualizados"><Metric icon={Eye} label="Visualizados" value={stats?.visualizados ?? "-"} tone="warn" testid="metric-visualizados" /></Link>
        <Link to="/admin/orcamentos?status=aprovado"><Metric icon={CheckCircle2} label="Aprovados" value={stats?.aprovados ?? "-"} tone="success" testid="metric-aprovados" /></Link>
        <Link to="/admin/orcamentos?status=recusado"><Metric icon={XCircle} label="Recusados" value={stats?.recusados ?? "-"} tone="danger" testid="metric-recusados" /></Link>
        <Link to="/admin/orcamentos?status=pendentes"><Metric icon={Clock} label="Pendentes" value={stats?.pendentes ?? "-"} tone="accent" testid="metric-pendentes" /></Link>
      </div>

      {/* Comprovantes PIX pendentes */}
      {comprovantes.length > 0 && (
        <div className="bbh-card p-5 border-2" style={{ borderColor: "#F59E0B", background: "#FFFBEB" }} data-testid="comprovantes-pendentes">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-700">⚠ Ação necessária</div>
              <h2 className="font-heading text-xl font-bold text-amber-900">
                {comprovantes.length} comprovante{comprovantes.length > 1 ? "s" : ""} PIX aguardando
              </h2>
            </div>
          </div>
          <ul className="space-y-2">
            {comprovantes.map((c) => (
              <li key={c.id} className="bg-white rounded-md border border-amber-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`comp-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">
                    Orçamento <span className="font-mono">{c.numero_orcamento}</span> · R$ {c.valor.toFixed(2).replace(".", ",")}
                  </div>
                  <div className="text-xs text-zinc-600 truncate">
                    {c.cliente?.nome || "Cliente"}
                    {c.observacao ? ` · ${c.observacao}` : ""}
                  </div>
                  <div className="text-[10px] text-zinc-400">{new Date(c.created_at).toLocaleString("pt-BR")}</div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setCompOpen(c)}
                    data-testid={`comp-ver-${c.id}`}
                    className="px-3 py-1.5 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                  >
                    Ver comprovante
                  </button>
                  <button
                    onClick={() => confirmarComp(c)}
                    data-testid={`comp-confirmar-${c.id}`}
                    className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => rejeitarComp(c)}
                    data-testid={`comp-rejeitar-${c.id}`}
                    className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                  >
                    Rejeitar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal visualizar comprovante */}
      {compOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setCompOpen(null)}>
          <div className="bg-white rounded-md p-4 max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading text-lg font-bold">Comprovante PIX</div>
                <div className="text-xs text-zinc-500">
                  Orçamento {compOpen.numero_orcamento} · R$ {compOpen.valor.toFixed(2).replace(".", ",")} · {compOpen.cliente?.nome || ""}
                </div>
              </div>
              <button onClick={() => setCompOpen(null)} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
            </div>
            {compOpen.arquivo_tipo === "application/pdf" ? (
              <iframe src={fileUrl((compOpen.arquivo_url || "").replace("/api/files/", ""))} className="w-full h-[70vh] rounded border" title="comprovante" />
            ) : (
              <img src={fileUrl((compOpen.arquivo_url || "").replace("/api/files/", ""))} alt="comprovante" className="w-full rounded border" />
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={() => rejeitarComp(compOpen)} className="flex-1 py-2.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-semibold">Rejeitar</button>
              <button onClick={() => confirmarComp(compOpen)} className="flex-1 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Confirmar pagamento</button>
            </div>
          </div>
        </div>
      )}

      <div className="bbh-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold">Orçamentos recentes</h2>
          <Link to="/admin/orcamentos" className="text-sm text-[#14243D] font-medium" data-testid="dashboard-ver-todos">
            Ver todos →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 text-sm">
            Nenhum orçamento ainda. Crie o primeiro.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {recent.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between py-3 gap-3"
                data-testid={`recent-orcamento-${o.numero_orcamento}`}
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm text-zinc-500">Nº {o.numero_orcamento}</div>
                  <div className="font-medium truncate">{o.cliente?.nome || "-"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-semibold">{money(o.valor_total)}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{o.status}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Link
                    to={`/admin/orcamentos/${o.id}`}
                    title="Editar"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-zinc-900 text-white text-xs font-semibold"
                    data-testid={`recent-editar-${o.numero_orcamento}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Editar</span>
                  </Link>
                  <Link
                    to={`/q/${o.token_publico}?admin=1`}
                    title="Visualizar como o cliente vê"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-zinc-300 text-zinc-700 text-xs font-semibold hover:bg-zinc-50"
                    data-testid={`recent-visualizar-${o.numero_orcamento}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Visualizar</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
